import React, { useState } from 'react';
import styled from 'styled-components';
import { getAuth } from 'firebase/auth';
import { API_URL } from '../../constants/string-constants';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: var(--clr-prim);
  padding: 2rem;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  color: var(--clr-sec);
  margin: 0;
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--clr-sec);
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const UserInfo = styled.div`
  background-color: var(--clr-bg);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border-left: 4px solid #ff4444;
`;

const UserIdText = styled.p`
  margin: 0;
  color: var(--clr-sec);
  font-family: monospace;
  font-size: 0.9rem;
  opacity: 0.8;
`;

const MessageText = styled.p`
  margin: 0.5rem 0 0 0;
  color: var(--clr-sec);
  font-style: italic;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: var(--clr-sec);
  font-weight: 500;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  background-color: white;
  color: #333;
  
  &:focus {
    outline: none;
    border-color: #ff4444;
  }
  
  &::placeholder {
    color: #999;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background-color: white;
  color: #333;
  
  &:focus {
    outline: none;
    border-color: #ff4444;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background-color: white;
  color: #333;
  
  &:focus {
    outline: none;
    border-color: #ff4444;
  }
  
  &::placeholder {
    color: #999;
  }
`;

const DurationInputGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
`;

const NumberInput = styled(Input)`
  flex: 1;
  min-width: 80px;
`;

const UnitSelect = styled(Select)`
  flex: 1;
  min-width: 120px;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  color: var(--clr-sec);
  font-size: 0.9rem;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: #666;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #555;
  }
`;

const BanButton = styled(Button)`
  background-color: #ff4444;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #dd3333;
  }
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  border: 1px solid #f5c6cb;
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  border: 1px solid #c3e6cb;
`;

const ModBanModal = ({ isOpen, onClose, message, room = 'general', onBanComplete }) => {
  const [internalNote, setInternalNote] = useState('');
  const [externalReason, setExternalReason] = useState('');
  const [banAmount, setBanAmount] = useState('3');
  const [banUnit, setBanUnit] = useState('days');
  const [silentBan, setSilentBan] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Helper function to convert ban duration to days
  const convertToDays = (amount, unit) => {
    const num = parseInt(amount);
    switch (unit) {
      case 'days':
        return num;
      case 'weeks':
        return num * 7;
      case 'months':
        return num * 30; // Approximate month
      case 'years':
        return num * 365; // Approximate year
      default:
        return num;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!externalReason.trim()) {
      setError('Please provide a reason for the ban (shown to user)');
      return;
    }

    // Validate ban amount
    const amount = parseInt(banAmount);
    if (!banAmount.trim() || isNaN(amount) || amount < 1) {
      setError('Please enter a valid ban duration');
      return;
    }

    // Convert to days and validate total duration
    const finalBanDuration = convertToDays(amount, banUnit);
    if (finalBanDuration > 3650) { // Max ~10 years
      setError('Ban duration cannot exceed 10 years');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = await getAuth().currentUser.getIdToken();
      
      const response = await fetch(`${API_URL}mod/ban`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: message.userId,
          reason: internalNote.trim(),
          externalReason: externalReason.trim(),
          room: room,
          banDuration: finalBanDuration,
          bannedMessage: message.messageText || null,
          silentBan: silentBan,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setSuccess(`User banned successfully for ${banAmount} ${banUnit} (${finalBanDuration} day(s))`);
        setInternalNote('');
        setExternalReason('');
        
        // Call onBanComplete if provided
        if (onBanComplete) {
          onBanComplete();
        }
        
        // Auto-close after 2 seconds
        setTimeout(() => {
          onClose();
          setSuccess('');
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to ban user');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setInternalNote('');
    setExternalReason('');
    setBanAmount('3');
    setBanUnit('days');
    setSilentBan(false);
    setError('');
    setSuccess('');
    onClose();
  };

  if (!isOpen || !message) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Ban User</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        <UserInfo>
          <UserIdText>User ID: {message.userId}</UserIdText>
          {message.messageText && (
            <MessageText>"{message.messageText}"</MessageText>
          )}
        </UserInfo>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="externalReason">Reason (shown to the banned user) *</Label>
            <TextArea
              id="externalReason"
              value={externalReason}
              onChange={(e) => setExternalReason(e.target.value)}
              placeholder="Enter the reason that will be shown to the banned user..."
              required
              disabled={loading}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="internalNote">Internal note (shown to other mods only)</Label>
            <TextArea
              id="internalNote"
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              placeholder="Enter internal notes for other moderators (optional)..."
              disabled={loading}
            />
          </FormGroup>

          <FormGroup>
            <Label>Ban Duration</Label>
            <DurationInputGroup>
              <NumberInput
                type="number"
                value={banAmount}
                onChange={(e) => setBanAmount(e.target.value)}
                placeholder="Amount"
                min="1"
                max="999"
                disabled={loading}
              />
              <UnitSelect
                value={banUnit}
                onChange={(e) => setBanUnit(e.target.value)}
                disabled={loading}
              >
                <option value="days">Days</option>
                <option value="weeks">Weeks</option>
                <option value="months">Months</option>
                <option value="years">Years</option>
              </UnitSelect>
            </DurationInputGroup>
          </FormGroup>

          <CheckboxGroup>
            <Checkbox
              type="checkbox"
              id="silentBan"
              checked={silentBan}
              onChange={(e) => setSilentBan(e.target.checked)}
              disabled={loading}
            />
            <CheckboxLabel htmlFor="silentBan">
              Silent ban (don't announce to chat)
            </CheckboxLabel>
          </CheckboxGroup>

          <ButtonGroup>
            <CancelButton type="button" onClick={handleClose} disabled={loading}>
              Cancel
            </CancelButton>
            <BanButton type="submit" disabled={loading || !externalReason.trim()}>
              {loading ? 'Banning...' : 'Ban User'}
            </BanButton>
          </ButtonGroup>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ModBanModal;